import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { canManageProductActions, hasPermission, hasYangonRegionAccess, isRootAdminUser } from 'src/lib/utils';
import { userSelector } from 'src/store/apps/slices/UserSlice';

/**
 * Route guard.
 *
 * - permission === null  → intentional authenticated-only (no WHAT check)
 * - permission === string → enforce hasPermission
 * - permission === undefined → misconfiguration; deny (do not silently open)
 */
export const RouteWithPermission = ({
  children,
  permission,
  requireProductMasterAccess = false,
  requireYangonAccess = false,
}) => {
  const { user, loading, isPendingUserDetails } = useSelector(userSelector);

  if (loading || !user || isPendingUserDetails) return null;

  if (!user || (typeof user === 'object' && Object.keys(user).length === 0)) {
    return <Navigate to="/auth/login" replace />;
  }

  // Explicit intentional open for authenticated users
  if (permission === null) {
    return children;
  }

  // Missing permission config must not become public
  if (permission === undefined) {
    return <Navigate to="/permission-denied" replace />;
  }

  const isRootAdmin = isRootAdminUser(user);

  if (!hasPermission(user?.permissions, permission, isRootAdmin)) {
    return <Navigate to="/permission-denied" replace />;
  }

  if (requireProductMasterAccess && !canManageProductActions(user)) {
    return <Navigate to="/permission-denied" replace />;
  }

  if (requireYangonAccess && !hasYangonRegionAccess(user)) {
    return <Navigate to="/permission-denied" replace />;
  }

  return children;
};
